import type { Ref } from 'vue';

import { checkStudentRoleInvite } from '~/graphql/documents/core';
import { CheckStudentRoleInviteReason } from '~/graphql/generated/graphql';

export interface StudentRoleInviteCheck {
  reason: CheckStudentRoleInviteReason;
  /** The invite's school, present even when the invite itself is expired or used. */
  school: { name: string; displayName: string } | null;
  invite: { id: string; email: string; school: { id: string; name: string; label: string; displayName: string } } | null;
}

const INVALID: StudentRoleInviteCheck = { reason: CheckStudentRoleInviteReason.Invalid, school: null, invite: null };

export const studentRoleInviteKey = (inviteCode: string | null) => `checkStudentRoleInvite:${inviteCode ?? ''}`;

export const useStudentRoleInvite = async (inviteCode: string | null): Promise<Ref<StudentRoleInviteCheck | undefined>> => {
  const client = useUrqlClient();

  const { data } = await useAsyncData<StudentRoleInviteCheck>(
    studentRoleInviteKey(inviteCode),
    async () => {
      if (!inviteCode) {
        return INVALID;
      }
      const result = await client.query(checkStudentRoleInvite, { input: { code: inviteCode } }, { requestPolicy: 'network-only' });
      const payload = result.data?.core?.org?.school?.checkStudentRoleInvite;
      if (!payload) {
        return INVALID;
      }
      if (payload.reason === CheckStudentRoleInviteReason.Valid) {
        if (!payload.id || !payload.school || !payload.email) {
          return INVALID;
        }
        return {
          reason: payload.reason,
          school: payload.school,
          invite: { id: payload.id, email: payload.email, school: payload.school },
        };
      }
      return { reason: payload.reason, school: payload.school ?? null, invite: null };
    },
  );

  return data;
};
